import React, { useState, useEffect, useMemo } from 'react';
import ClientSidebar from '../components/ClientSidebar';
import BookingModal from '../components/BookingModal';
import NotificationBell from '../components/NotificationBell';
import ChatBotTrigger from '../../components/ChatBotTrigger';

import { API_BASE } from '../../config/runtime';

const ClientTreatments = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [branchLoading, setBranchLoading] = useState(false);
  const [branchError, setBranchError] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [showSelectedPanel, setShowSelectedPanel] = useState(false);

  const user = JSON.parse(localStorage.getItem('user')) || null;
  const isLoggedIn = user && user.role?.toLowerCase() === 'client';
  const branchSelected = Boolean(selectedBranchId);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await fetch(`${API_BASE}/get_services.php`);
        const data = await response.json();
        if (data.success) setServices(data.services || []);
      } catch {
        setServices([]);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  useEffect(() => {
    if (!selectionMode) return;
    const fetchLookupData = async () => {
      setBranchLoading(true);
      setBranchError('');
      try {
        const [branchesResponse, categoriesResponse] = await Promise.all([
          fetch(`${API_BASE}/get_branches.php`),
          fetch(`${API_BASE}/manage_categories.php`)
        ]);
        const [branchesData, categoriesData] = await Promise.all([
          branchesResponse.json(),
          categoriesResponse.json()
        ]);
        if (branchesData.success) {
          setBranches(branchesData.branches || []);
        } else {
          setBranches([]);
          setBranchError(branchesData.message || 'Unable to load branches.');
        }
        if (categoriesData.success) {
          setCategories(categoriesData.categories || []);
        } else {
          setCategories([]);
        }
      } catch {
        setBranches([]);
        setCategories([]);
        setBranchError('Unable to load branches.');
      } finally {
        setBranchLoading(false);
      }
    };
    fetchLookupData();
  }, [selectionMode]);

  const formatDuration = (totalMinutes) => {
    if (totalMinutes === null || totalMinutes === undefined || totalMinutes === 0) return null;
    if (totalMinutes < 60) return `${totalMinutes} mins`;
    const hours = totalMinutes / 60;
    const displayHours = hours % 1 === 0 ? hours : hours.toFixed(1);
    return `${displayHours} ${hours === 1 ? 'hr' : 'hrs'}`;
  };

  const allowedCategoryIds = useMemo(() => {
    if (!branchSelected || categories.length === 0) return null;
    const selectedId = Number(selectedBranchId);
    return categories
      .filter((cat) => {
        const branchIds = Array.isArray(cat.branch_ids) ? cat.branch_ids : [];
        if (!branchIds.length) return true;
        return branchIds.some((branchId) => Number(branchId) === selectedId);
      })
      .map((cat) => Number(cat.category_id));
  }, [branchSelected, categories, selectedBranchId]);

  const filteredServices = useMemo(() => {
    if (!branchSelected) return [];
    if (!services.length) return [];
    if (!allowedCategoryIds) return services;
    return services.filter((service) => allowedCategoryIds.includes(Number(service.category_id)));
  }, [branchSelected, services, allowedCategoryIds]);

  const visibleServices = useMemo(() => {
    if (!selectionMode) return services;
    if (!branchSelected) return [];
    return filteredServices;
  }, [selectionMode, branchSelected, filteredServices, services]);

  const groupedServices = useMemo(() => {
    return visibleServices.reduce((acc, service) => {
      const cat = service.category_name || 'General Services';
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(service);
      return acc;
    }, {});
  }, [visibleServices]);

  useEffect(() => {
    if (!selectionMode) return;
    if (!branchSelected) {
      if (selectedServices.length > 0) setSelectedServices([]);
      return;
    }
    const allowedIds = new Set(filteredServices.map((service) => service.id));
    const nextSelected = selectedServices.filter((service) => allowedIds.has(service.id));
    if (nextSelected.length !== selectedServices.length) setSelectedServices(nextSelected);
  }, [selectionMode, branchSelected, filteredServices, selectedServices]);

  const handleToggleService = (service) => {
    if (!selectionMode || !branchSelected) return;
    if (selectedServices.find(s => s.id === service.id)) {
      setSelectedServices(selectedServices.filter(s => s.id !== service.id));
    } else {
      setSelectedServices([...selectedServices, service]);
    }
  };

  const handleToggleSelectionMode = () => {
    if (selectionMode) {
      setSelectionMode(false);
      setSelectedServices([]);
      setSelectedBranchId('');
      setBranchError('');
      setShowSelectedPanel(false);
    } else {
      setSelectionMode(true);
    }
  };

  const handleBookClick = () => {
    if (selectedServices.length > 0) setShowBookingModal(true);
  };

  const handleBookingComplete = () => {
    setSelectedServices([]);
    setSelectionMode(false);
    setSelectedBranchId('');
    setShowSelectedPanel(false);
  };

  const getStoredClientUser = () => {
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return (parsed?.role || '').toLowerCase() === 'client' ? parsed : null;
    } catch {
      return null;
    }
  };

  const clientUser = getStoredClientUser();
  const totalPrice = selectedServices.reduce((sum, s) => sum + Number(s.price), 0);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 300);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen hygea-app-bg">
      <ClientSidebar
        isOpen={isSidebarOpen}
        onOpenChange={setIsSidebarOpen}
        clientName={clientUser?.full_name}
        clientEmail={clientUser?.email}
        clientAvatar={clientUser?.avatar_url}
        onLogout={() => { window.location.href = '/'; }}
      />

      {/* Notification Bell */}
      <div className="fixed top-4 right-4 sm:right-6 z-50">
        <NotificationBell isScrolled={false} forceBrown={true} />
      </div>

      <div className="transition-all duration-300">
        <div className="px-4 py-6 sm:p-8">
          <div className="max-w-6xl mx-auto">

            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-6 sm:mb-8">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">Our Treatments</h2>
                <p className="text-gray-500 mt-1 text-sm sm:text-base">Browse our selection of wellness services</p>
              </div>
              <div className="flex gap-2 sm:gap-3">
                {selectionMode && selectedServices.length > 0 && (
                  <button
                    className="flex-1 sm:flex-none px-4 sm:px-6 py-2.5 bg-[#984e5c] text-white rounded-xl font-bold text-sm hover:bg-[#7a3f4d] active:scale-95 transition-all"
                    onClick={handleBookClick}
                  >
                    Book ({selectedServices.length})
                  </button>
                )}
                <button
                  className={`flex-1 sm:flex-none px-4 sm:px-6 py-2.5 rounded-xl font-bold text-sm active:scale-95 transition-all ${
                    selectionMode ? 'bg-gray-200 text-gray-700' : 'bg-black text-white'
                  }`}
                  onClick={handleToggleSelectionMode}
                >
                  {selectionMode ? 'Cancel' : 'Select Services'}
                </button>
              </div>
            </div>

            {/* Branch Selector */}
            {selectionMode && (
              <div className="mb-5 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-4">
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Select Branch</p>
                    <p className="mt-0.5 text-xs text-gray-500">Choose a branch to view available services.</p>
                  </div>
                  <div className="w-full sm:min-w-[220px] sm:flex-1 sm:max-w-xs">
                    <select
                      value={selectedBranchId}
                      onChange={(e) => {
                        setSelectedBranchId(e.target.value);
                        setSelectedServices([]);
                      }}
                      className="w-full rounded-xl border border-gray-200 bg-white px-4 py-3 sm:py-2.5 text-sm sm:text-xs font-bold uppercase tracking-wider text-gray-700 focus:border-[#984e5c] focus:outline-none focus:ring-2 focus:ring-[#984e5c]/20 appearance-none"
                    >
                      <option value="">Choose a branch</option>
                      {branches.map((branch) => (
                        <option key={branch.branch_id} value={branch.branch_id}>
                          {branch.branch_name}{branch.address ? ` - ${branch.address}` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                {branchLoading && <p className="mt-3 text-xs text-gray-400">Loading branches...</p>}
                {!branchLoading && branchError && <p className="mt-3 text-xs text-red-500">{branchError}</p>}
                {!branchLoading && !branchError && branches.length === 0 && (
                  <p className="mt-3 text-xs text-gray-400">No branches available.</p>
                )}
              </div>
            )}

            {/* Selected Services Panel — collapsible on mobile */}
            {selectionMode && selectedServices.length > 0 && (
              <div className="mb-5 bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Collapse toggle for mobile */}
                <button
                  className="w-full flex justify-between items-center px-4 py-3 sm:cursor-default"
                  onClick={() => setShowSelectedPanel(prev => !prev)}
                >
                  <h3 className="text-sm font-bold text-gray-600">
                    Selected ({selectedServices.length}) —&nbsp;
                    <span className="text-[#984e5c]">&#8369;{totalPrice.toLocaleString()}</span>
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelectedServices([]); }}
                      className="text-xs text-gray-400 hover:text-red-500 px-2 py-1 rounded"
                    >
                      Clear All
                    </button>
                    {/* Chevron icon for mobile */}
                    <span className={`sm:hidden text-gray-400 transition-transform duration-200 ${showSelectedPanel ? 'rotate-180' : ''}`}>
                      ▾
                    </span>
                  </div>
                </button>

                {/* Always visible on sm+, toggleable on mobile */}
                <div className={`${showSelectedPanel ? 'block' : 'hidden'} sm:block border-t border-gray-100 px-4 pb-4 pt-3`}>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {selectedServices.map((service) => (
                      <div
                        key={service.id}
                        className="flex items-center gap-2 bg-gray-50 px-3 py-1.5 rounded-lg border border-gray-200"
                      >
                        <span className="text-xs font-medium text-gray-700">{service.name}</span>
                        <span className="text-xs font-bold text-[#984e5c]">&#8369;{Number(service.price).toLocaleString()}</span>
                        <button
                          onClick={() => setSelectedServices(selectedServices.filter(s => s.id !== service.id))}
                          className="w-4 h-4 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 hover:bg-red-100 hover:text-red-500 text-xs leading-none"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="pt-2 border-t border-gray-100 flex justify-between items-center">
                    <span className="text-sm font-bold text-gray-500">Total</span>
                    <span className="text-xl font-bold text-[#984e5c]">&#8369;{totalPrice.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Service Grid */}
            {loading ? (
              <div className="text-center py-16">
                <p className="text-gray-500">Loading services...</p>
              </div>
            ) : selectionMode && !branchSelected ? (
              <div className="rounded-xl border border-dashed border-gray-200 bg-white p-10 text-center text-sm text-gray-500">
                Select a branch to view available services.
              </div>
            ) : Object.keys(groupedServices).length === 0 ? (
              <div className="rounded-xl border border-dashed border-gray-200 bg-white p-10 text-center text-sm text-gray-500">
                {selectionMode && branchSelected ? 'No services available for this branch.' : 'No services available.'}
              </div>
            ) : (
              <div className="space-y-10 sm:space-y-12">
                {Object.keys(groupedServices).map((category) => (
                  <div key={category}>
                    <div className="flex items-center justify-center mb-5 sm:mb-6">
                      <div className="flex-1 h-px bg-gray-200"></div>
                      <h3 className="px-4 text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        {category}
                      </h3>
                      <div className="flex-1 h-px bg-gray-200"></div>
                    </div>

                    {/* Responsive grid: 1 col mobile → 2 col sm → 3 col lg → 4 col xl */}
                    <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                      {groupedServices[category].map((service) => {
                        const isSelected = Boolean(selectedServices.find(s => s.id === service.id));
                        return (
                          <div
                            key={service.id}
                            onClick={() => handleToggleService(service)}
                            className={`bg-white rounded-2xl shadow-sm border overflow-hidden relative transition-all duration-150
                              ${selectionMode ? 'cursor-pointer active:scale-[0.98]' : 'cursor-default'}
                              ${selectionMode && isSelected
                                ? 'border-[#984e5c] ring-2 ring-[#984e5c]'
                                : 'border-gray-200'
                              }`}
                          >
                            {/* Selection checkmark */}
                            {selectionMode && (
                              <div
                                className={`absolute top-2 left-2 w-5 h-5 rounded-full flex items-center justify-center border-2 z-10 transition-colors
                                  ${isSelected ? 'bg-[#984e5c] border-[#984e5c]' : 'bg-white border-gray-300'}`}
                              >
                                {isSelected && <span className="text-white text-xs leading-none">✓</span>}
                              </div>
                            )}

                            {/* Image — taller on mobile for easier tap target */}
                            <div className="h-44 sm:h-40 bg-gray-100">
                              {service.image
                                ? <img src={service.image} alt={service.name} className="w-full h-full object-cover" />
                                : <div className="w-full h-full flex items-center justify-center text-gray-400 text-sm">Hygea</div>
                              }
                            </div>

                            <div className="p-4">
                              <h4 className="font-bold text-gray-800 mb-1 text-sm sm:text-base leading-snug">{service.name}</h4>
                              <p className="text-xs text-gray-500 mb-3 line-clamp-2">
                                {service.description || 'A signature wellness experience.'}
                              </p>
                              <div className="flex items-center justify-between">
                                <span className="text-base sm:text-lg font-bold text-[#984e5c]">
                                  &#8369;{Number(service.price).toLocaleString()}
                                </span>
                                {formatDuration(service.duration_minutes) && (
                                  <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded">
                                    {formatDuration(service.duration_minutes)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Bottom padding so floating elements don't overlap content on mobile */}
            <div className="h-6 sm:h-0" />
          </div>
        </div>
      </div>

      {/* Sticky bottom bar on mobile when services are selected */}
      {selectionMode && selectedServices.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 sm:hidden bg-white border-t border-gray-200 px-4 py-3 flex items-center gap-3 shadow-lg">
          <div className="flex-1 min-w-0">
            <p className="text-xs text-gray-500 truncate">{selectedServices.length} service{selectedServices.length > 1 ? 's' : ''} selected</p>
            <p className="text-base font-bold text-[#984e5c]">&#8369;{totalPrice.toLocaleString()}</p>
          </div>
          <button
            onClick={handleBookClick}
            className="px-5 py-2.5 bg-[#984e5c] text-white rounded-xl font-bold text-sm hover:bg-[#7a3f4d] active:scale-95 transition-all"
          >
            Book Now
          </button>
        </div>
      )}

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 z-50 w-10 h-10 bg-[#984e5c] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#7a3f4d] active:scale-95 transition-all"
          aria-label="Scroll to top"
        >
          &#8593;
        </button>
      )}

      <BookingModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        selectedServices={selectedServices}
        services={services}
        onServicesChange={setSelectedServices}
        defaultBranchId={selectedBranchId}
        user={user}
        isLoggedIn={isLoggedIn}
        onBookingComplete={handleBookingComplete}
      />
      <ChatBotTrigger />
    </div>
  );
};

export default ClientTreatments;
